'use client';

import { useEffect, useId, useReducer, useRef, useState } from 'react';
import { REQUEST_STATUSES, REQUEST_STATUS_LABELS } from '../lib/requests-shared';
import type { FeatureRequest, FeatureRequestDetail, FeatureRequestsApi, RequestFilters, RequestStatus } from '../lib/requests-shared';
import { composerReducer, initialComposer, validDescription, validTitle } from './request-composer';

export type { FeatureRequestsApi } from '../lib/requests-shared';
export interface FeatureRequestsProps {
  api: FeatureRequestsApi;
  onClose?: () => void;
  startInComposer?: boolean;
  onSubmissionAccessChange?: (allowed: boolean) => void;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
}

function requestDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function FeatureRequests({ api, onClose, startInComposer = false, onSubmissionAccessChange }: FeatureRequestsProps) {
  const uid = useId();
  const [composer, dispatch] = useReducer(composerReducer, undefined, () => initialComposer());
  const [composing, setComposing] = useState(startInComposer);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [canSubmit, setCanSubmit] = useState(true);
  const [filters, setFilters] = useState<RequestFilters>({ order: 'votes', query: '', status: '' });
  const [query, setQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const actionLock = useRef(false);
  const [pendingVotes, setPendingVotes] = useState<Set<string>>(new Set());
  const voteLocks = useRef(new Set<string>());
  const [detail, setDetail] = useState<FeatureRequestDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailSequence = useRef(0);
  const [detailError, setDetailError] = useState('');
  const [enhancementDrafts, setEnhancementDrafts] = useState<Record<string, { description: string; submissionId: string }>>({});
  const [refresh, setRefresh] = useState(0);
  const listSequence = useRef(0);
  const loadMoreLock = useRef(false);
  const descriptionField = useRef<HTMLTextAreaElement>(null);
  const detailHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!startInComposer) return;
    setComposing(true);
    ++detailSequence.current;
    setDetail(null);
    setDetailLoading(false);
    setDetailError('');
    setActionError('');
  }, [startInComposer]);
  useEffect(() => {
    const sequence = ++listSequence.current;
    setLoading(true);
    setListError('');
    void api.list({ ...filters, offset: 0 }).then((page) => {
      if (sequence !== listSequence.current) return;
      setRequests(page.requests);
      setHasMore(page.hasMore);
      setCanSubmit(page.canSubmit !== false);
      onSubmissionAccessChange?.(page.canSubmit !== false);
    }).catch((error: unknown) => {
      if (sequence === listSequence.current) setListError(errorText(error));
    }).finally(() => { if (sequence === listSequence.current) setLoading(false); });
    return () => { ++listSequence.current; };
  }, [api, filters, refresh, onSubmissionAccessChange]);

  function replaceRequest(request: FeatureRequest) {
    setRequests((items) => items.map((item) => item.id === request.id ? request : item));
    dispatchComparisonVote(request);
    setDetail((current) => current?.id === request.id ? { ...current, ...request } : current);
  }

  // Comparison results also show current server vote counts.
  const [matchOverrides, setMatchOverrides] = useState<Record<string, FeatureRequest>>({});
  function dispatchComparisonVote(request: FeatureRequest) {
    setMatchOverrides((current) => ({ ...current, [request.id]: request }));
  }

  async function vote(request: FeatureRequest) {
    if (voteLocks.current.has(request.id)) return;
    voteLocks.current.add(request.id);
    setPendingVotes(new Set(voteLocks.current));
    setActionError('');
    try { replaceRequest(await api.vote(request.id, !request.voted)); }
    catch (error) { setActionError(errorText(error)); }
    finally { voteLocks.current.delete(request.id); setPendingVotes(new Set(voteLocks.current)); }
  }

  async function openDetail(id: string) {
    const sequence = ++detailSequence.current;
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    setActionError('');
    try {
      const loaded = await api.detail(id);
      if (sequence === detailSequence.current) setDetail(loaded);
    } catch (error) { if (sequence === detailSequence.current) setDetailError(errorText(error)); }
    finally { if (sequence === detailSequence.current) setDetailLoading(false); }
  }

  function closeDetail() {
    ++detailSequence.current;
    setDetail(null);
    setDetailLoading(false);
    setDetailError('');
    setActionError('');
  }

  async function runAction(action: () => Promise<void>) {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(true);
    setActionError('');
    setNotice('');
    try { await action(); } catch (error) { setActionError(errorText(error)); }
    finally { actionLock.current = false; setBusy(false); }
  }

  async function compare() {
    if (!validDescription(composer.description)) return;
    await runAction(async () => {
      const comparison = await api.compare(composer.description.trim());
      setMatchOverrides({});
      dispatch({ type: 'compared', comparison });
    });
  }

  async function submit() {
    if (!validDescription(composer.description) || !validTitle(composer.title)) return;
    await runAction(async () => {
      const submissionId = composer.submissionId || crypto.randomUUID();
      if (!composer.submissionId) dispatch({ type: 'title', value: composer.title, submissionId });
      const result = await api.create({ title: composer.title.trim(), description: composer.description.trim(), submissionId, confirmDistinct: composer.confirmDistinct });
      if (!result.created) {
        setMatchOverrides({});
        dispatch({ type: 'compared', comparison: result.comparison });
        setNotice('We checked again before submitting. Review these results, then confirm if your request is distinct.');
        return;
      }
      dispatch({ type: 'reset', submissionId: crypto.randomUUID() });
      setComposing(false);
      setDetail(null);
      setNotice('Your suggestion was submitted for review. It will appear here after approval.');
      setRefresh((value) => value + 1);
    });
  }

  async function enhance() {
    if (!detail) return;
    const draft = enhancementDrafts[detail.id];
    if (!draft || !validDescription(draft.description)) return;
    const id = detail.id;
    await runAction(async () => {
      const updated = await api.enhance(id, { description: draft.description.trim(), submissionId: draft.submissionId });
      setDetail((current) => current?.id === id ? updated : current);
      replaceRequest(updated);
      setEnhancementDrafts((current) => ({ ...current, [id]: { description: '', submissionId: crypto.randomUUID() } }));
      setNotice('Your enhancement was added.');
    });
  }

  async function loadMore() {
    if (loadMoreLock.current || loading) return;
    loadMoreLock.current = true;
    const sequence = listSequence.current;
    setLoading(true);
    setListError('');
    try {
      const page = await api.list({ ...filters, offset: requests.length });
      if (sequence !== listSequence.current) return;
      setRequests((current) => [...current, ...page.requests.filter((item) => !current.some((existing) => existing.id === item.id))]);
      setHasMore(page.hasMore);
    } catch (error) { if (sequence === listSequence.current) setListError(errorText(error)); }
    finally { loadMoreLock.current = false; if (sequence === listSequence.current) setLoading(false); }
  }

  function voteButton(request: FeatureRequest) {
    return <button type="button" className="bay-fr-vote" aria-pressed={request.voted} aria-label={`${request.voted ? 'Remove vote for' : 'Vote for'} ${request.title} (${request.voteCount} votes)`} disabled={pendingVotes.has(request.id)} onClick={() => { void vote(request); }}><span aria-hidden="true">↑</span> {request.voteCount}</button>;
  }

  function requestCard(request: FeatureRequest, reason?: string) {
    return <article className="bay-fr-card" key={request.id}>
      {voteButton(request)}
      <div className="bay-fr-card-content">
        <button type="button" className="bay-fr-request-title" onClick={() => { void openDetail(request.id); }}>{request.title}</button>
        <p className="bay-fr-excerpt">{request.description}</p>
        <div className="bay-fr-meta"><span className="bay-fr-status" data-status={request.status}>{REQUEST_STATUS_LABELS[request.status]}</span><time dateTime={request.createdAt}>{requestDate(request.createdAt)}</time><span>{request.enhancementCount} enhancements</span></div>
        {reason && <p className="bay-fr-match-reason">{reason}</p>}
        {reason && <button type="button" className="bay-fr-text-button" onClick={() => { void openDetail(request.id); }}>View or add an enhancement</button>}
      </div>
    </article>;
  }

  const viewingDetail = detail !== null || detailLoading || detailError !== '';
  const enhancement = detail ? enhancementDrafts[detail.id]?.description ?? '' : '';

  useEffect(() => {
    const target = detail ? detailHeading.current : composing && !viewingDetail && composer.stage === 'describe' ? descriptionField.current : null;
    if (target && target.getClientRects().length > 0) target.focus();
  }, [detail?.id, composing, viewingDetail, composer.stage, startInComposer]);

  return <section className="bay-fr" aria-labelledby={`${uid}-heading`}>
    <header className="bay-fr-header"><div><h2 id={`${uid}-heading`}>Feature Requests</h2><p>Share an idea. Help shape what comes next.</p></div>{onClose && <button type="button" className="bay-fr-icon-button" onClick={onClose} aria-label="Close feature requests">×</button>}</header>
    {notice && <p className="bay-fr-notice" role="status">{notice}</p>}
    {actionError && <p className="bay-fr-error" role="alert">{actionError}</p>}
    {viewingDetail ? <div className="bay-fr-detail">
      <button type="button" className="bay-fr-text-button" onClick={closeDetail}>← {composing ? 'Back to your idea' : 'Back to requests'}</button>
      {detailLoading && <p role="status">Loading request…</p>}
      {detailError && <p className="bay-fr-error" role="alert">Could not load this request. {detailError}</p>}
      {detail && <>
        <div className="bay-fr-detail-title">{voteButton(detail)}<h3 ref={detailHeading} tabIndex={-1}>{detail.title}</h3></div>
        <span className="bay-fr-status" data-status={detail.status}>{REQUEST_STATUS_LABELS[detail.status]}</span>
        <p className="bay-fr-description">{detail.description}</p>
        <p className="bay-fr-meta">Submitted <time dateTime={detail.createdAt}>{requestDate(detail.createdAt)}</time></p>
        <h4>Enhancements ({detail.enhancementCount})</h4>
        {detail.enhancements.length === 0 && <p className="bay-fr-muted">No enhancements yet. What would you add?</p>}
        {detail.enhancements.map((item) => <article className="bay-fr-enhancement" key={item.id}><div className="bay-fr-meta"><strong>{item.authorName}</strong><time dateTime={item.createdAt}>{requestDate(item.createdAt)}</time></div><p className="bay-fr-description">{item.description}</p></article>)}
        <form onSubmit={(event) => { event.preventDefault(); void enhance(); }}>
          <label htmlFor={`${uid}-enhancement`}>Add an enhancement</label>
          <p className="bay-fr-help" id={`${uid}-enhancement-help`}>Explain what your idea adds to this request. 20–4,000 characters.</p>
          <textarea id={`${uid}-enhancement`} aria-describedby={`${uid}-enhancement-help`} rows={5} maxLength={4000} value={enhancement} disabled={busy} onChange={(event) => {
            const id = detail.id; const value = event.target.value;
            setEnhancementDrafts((current) => ({ ...current, [id]: { description: value, submissionId: current[id]?.description.trim() === value.trim() ? current[id].submissionId : crypto.randomUUID() } }));
          }} />
          <button type="submit" className="bay-fr-primary" disabled={busy || !validDescription(enhancement)}>{busy ? 'Saving…' : 'Add enhancement'}</button>
        </form>
      </>}
    </div> : composing && canSubmit ? <div className="bay-fr-composer">
      <button type="button" className="bay-fr-text-button" onClick={() => { setComposing(false); setActionError(''); }}>← Browse requests (keep draft)</button>
      <h3>{composer.stage === 'describe' ? 'What would you like to do?' : composer.stage === 'matches' ? 'Do any of these cover your idea?' : 'Create your feature request'}</h3>
      {composer.stage === 'describe' ? <form onSubmit={(event) => { event.preventDefault(); void compare(); }}>
        <label htmlFor={`${uid}-description`}>Describe the feature</label>
        <p className="bay-fr-help" id={`${uid}-description-help`}>Tell us the problem and what would help. We’ll search for similar requests before you submit. 20–4,000 characters.</p>
        <textarea ref={descriptionField} id={`${uid}-description`} aria-describedby={`${uid}-description-help`} rows={7} maxLength={4000} value={composer.description} disabled={busy} onChange={(event) => dispatch({ type: 'description', value: event.target.value, submissionId: crypto.randomUUID() })} />
        <span className="bay-fr-count">{composer.description.trim().length}/4,000</span>
        <button className="bay-fr-primary" type="submit" disabled={busy || !validDescription(composer.description)}>{busy ? 'Searching…' : 'Check similar requests'}</button>
        {actionError && <button type="button" className="bay-fr-text-button" onClick={() => { dispatch({ type: 'distinct' }); setActionError(''); }}>Continue with a new request without comparison</button>}
      </form> : <>
        <div className="bay-fr-draft"><p className="bay-fr-description">{composer.description}</p><button type="button" className="bay-fr-text-button" disabled={busy} onClick={() => { dispatch({ type: 'edit' }); setActionError(''); }}>Edit description</button></div>
        {composer.stage === 'matches' ? <>
          <p className="bay-fr-help">These requests have similar wording. Vote for one, add an enhancement, or create a distinct request.</p>
          {composer.comparison?.matches.map((match) => requestCard(matchOverrides[match.request.id] ?? match.request, match.reason))}
          <button type="button" className="bay-fr-primary" onClick={() => dispatch({ type: 'distinct' })}>My idea is different — create a new request</button>
        </> : <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          {composer.comparison && composer.comparison.matches.length === 0 && <p className="bay-fr-help">No similar requests found by text search.</p>}
          <label htmlFor={`${uid}-title`}>Give your request a title</label>
          <input id={`${uid}-title`} value={composer.title} minLength={3} maxLength={120} disabled={busy} onChange={(event) => dispatch({ type: 'title', value: event.target.value, submissionId: crypto.randomUUID() })} />
          <p className="bay-fr-help">A short, specific title (3–120 characters).</p>
          <button type="submit" className="bay-fr-primary" disabled={busy || !validTitle(composer.title)}>{busy ? 'Submitting…' : 'Submit feature request'}</button>
        </form>}
      </>}
    </div> : <>
      {!canSubmit && <p className="bay-fr-help">You can browse and vote on approved suggestions. New submissions aren’t available for your account.</p>}
      <div className="bay-fr-toolbar">{canSubmit && <button type="button" className="bay-fr-primary" onClick={() => { setComposing(true); setActionError(''); setNotice(''); }}>{composer.description ? 'Continue your idea' : 'Suggest a feature'}</button>}
        <form className="bay-fr-search" onSubmit={(event) => { event.preventDefault(); setFilters((current) => ({ ...current, query: query.trim() })); }}><label htmlFor={`${uid}-search`}>Search requests</label><div><input id={`${uid}-search`} type="search" placeholder="Search by words or phrases" value={query} onChange={(event) => setQuery(event.target.value)} /><button type="submit">Search</button></div></form>
        <div className="bay-fr-filters"><label>Sort<select value={filters.order} onChange={(event) => setFilters((current) => ({ ...current, order: event.target.value === 'latest' ? 'latest' : 'votes' }))}><option value="votes">Most voted</option><option value="latest">Latest</option></select></label><label>Status<select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as RequestStatus | '' }))}><option value="">All statuses</option>{REQUEST_STATUSES.map((status) => <option value={status} key={status}>{REQUEST_STATUS_LABELS[status]}</option>)}</select></label></div>
      </div>
      {listError && <div className="bay-fr-error" role="alert"><p>Could not load requests. {listError}</p><button type="button" onClick={() => setRefresh((value) => value + 1)}>Retry</button></div>}
      {loading && <p role="status" className="bay-fr-muted">Loading requests…</p>}
      {!loading && !listError && requests.length === 0 && <p className="bay-fr-empty">{filters.query || filters.status ? 'No requests match these filters.' : canSubmit ? 'No approved suggestions yet. Share the first idea.' : 'No approved suggestions yet.'}</p>}
      {!listError && requests.map((request) => requestCard(request))}
      {hasMore && !listError && <button type="button" className="bay-fr-load-more" disabled={loading} onClick={() => { void loadMore(); }}>Load more requests</button>}
    </>}
  </section>;
}
